/**
 * A contact suggestion as every reader sees it: "We found 12 people from
 * Proving Ground you can add to your contacts", the people, and the one verb
 * that adds them (`proactiveCalendarIntelligence.suggestContactsToAdd`).
 *
 * Read from a stored row (extras lifted onto it) or a feed item (extras nested
 * under `extraFields`), so the server presentation, the row and the add route
 * agree on what the suggestion holds. Adding writes Smart Manager contacts in
 * the user's silo, never a provider's address book.
 *
 * Pure.
 */

export type ContactSuggestionCardPerson = {
  /** Lower-case address the contact is added under. */
  email: string;
  /** The name the calendar sent, or "" when it sent none. */
  name: string;
};

export type ContactSuggestionCard = {
  sourceName: string;
  people: ContactSuggestionCardPerson[];
};

export type AddSuggestedContactsRequest = { notificationId: string };

export type AddSuggestedContactsErrorCode =
  | "unauthorized"
  | "invalid_request"
  | "invalid_json"
  | "request_too_large"
  | "not_found"
  | "suggestion_expired"
  | "contacts_unavailable"
  | "internal_error";

export type AddSuggestedContactsResponse =
  | { success: true; added: number; alreadyContacts: number }
  | { success: false; error: AddSuggestedContactsErrorCode };

/** How many people the expanded row lists before "and N more". */
export const CONTACT_SUGGESTION_REVIEW_SHOWN = 8;

type UnknownRecord = Record<string, unknown>;

const record = (value: unknown): UnknownRecord | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as UnknownRecord) : null;

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");

const EMAIL = /^[^\s@<>]+@[^\s@<>]+$/;

/** The suggestion this row offers, or null when the row is not one or names nobody. */
export function readContactSuggestionCard(item: unknown): ContactSuggestionCard | null {
  const row = record(item);
  if (!row) return null;
  const fields = { ...record(row.extraFields), ...row };
  if (text(fields.proactiveInsightType) !== "contact_suggestion" && text(fields.source) !== "contact_suggestion") return null;
  const seen = new Set<string>();
  const people: ContactSuggestionCardPerson[] = [];
  for (const value of Array.isArray(fields.people) ? fields.people : []) {
    const person = record(value);
    const email = text(person?.email).toLowerCase();
    if (!EMAIL.test(email) || seen.has(email)) continue;
    seen.add(email);
    const name = text(person?.name);
    people.push({ email, name: name.includes("@") ? "" : name });
  }
  if (people.length === 0) return null;
  return { sourceName: text(fields.sourceName), people };
}

/** The verb that adds everyone: "Add 12". */
export const addContactsLabel = (count: number) => `Add ${count}`;

/** The people an open suggestion lists, and how many more it holds ("and 4 more"). */
export function contactSuggestionReview(
  card: ContactSuggestionCard,
  shown = CONTACT_SUGGESTION_REVIEW_SHOWN,
): { people: ContactSuggestionCardPerson[]; more: number } {
  const people = card.people.slice(0, Math.max(0, shown));
  return { people, more: card.people.length - people.length };
}
